import React, { useState, useEffect, useCallback } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
} from "reactstrap";
import classnames from "classnames";
import { APIClient } from "../../helpers/api_helper";
import { toast } from "react-toastify";
import ModalConfirmacao from "./ModalConfirmacao";
import { NumericFormat } from "react-number-format";

const apiClient = new APIClient();

const SidebarCampanha = ({ isOpen, toggle, campanha, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [modalConfirmacaoOpen, setModalConfirmacaoOpen] = useState(false);
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    validade: "",
  });
  const [novoVoucher, setNovoVoucher] = useState({
    tipo: "fixo",
    codigo: "",
    valor: "",
    quantidade: "",
    parceiro_id: "",
  });
  const [parceiros, setParceiros] = useState([]);

  const fetchParceiros = useCallback(async () => {
    try {
      const response = await apiClient.get("/parceiros");
      if (response?.dados) {
        setParceiros(response.dados);
      }
    } catch (error) {
      console.error("Erro ao buscar parceiros:", error);
    }
  }, []);

  useEffect(() => {
    const fetch = async () => {
      await fetchParceiros();
      if (campanha) {
        setFormData({
          nome: campanha.nome || "",
          descricao: campanha.descricao || "",
          validade: campanha.validade ? campanha.validade.split("T")[0] : "",
        });
      }
    };
    fetch();
  }, [campanha, fetchParceiros]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "descricao" && value.length > 600) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVoucherChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'codigo') {
      // Remove acentos e caracteres especiais, converte para maiúsculas
      const formattedValue = value
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove acentos
        .replace(/[^A-Z0-9_]/g, ''); // Permite apenas letras, números e underscore
      
      setNovoVoucher((prev) => ({ ...prev, [name]: formattedValue }));
    } else {
      setNovoVoucher((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiClient.put(
        `/campanhas-voucher/${campanha.id}`,
        formData
      );
      if (response?.dados) {
        toast.success("Campanha atualizada com sucesso!");
        onSuccess();
      } else {
        toast.error(response?.mensagem || "Erro ao atualizar campanha");
      }
    } catch (error) {
      toast.error("Erro ao atualizar campanha");
    }

    setLoading(false);
  };

  const handleCriarVoucher = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...novoVoucher,
        campanha_id: campanha.id,
        valor: parseFloat(novoVoucher.valor),
        quantidade: parseInt(novoVoucher.quantidade),
      };

      const response = await apiClient.post(
        "/campanhas-voucher/criar-vouchers",
        payload
      );
      if (response?.dados) {
        toast.success("Voucher criado com sucesso!");
        onSuccess();
        setNovoVoucher({
          tipo: "fixo",
          codigo: "",
          valor: "",
          quantidade: "",
          parceiro_id: "",
        });
      } else {
        toast.error(response?.mensagem || "Erro ao criar voucher");
      }
    } catch (error) {
      toast.error("Erro ao criar voucher");
    }

    setLoading(false);
  };

  const toggleModalConfirmacao = () =>
    setModalConfirmacaoOpen(!modalConfirmacaoOpen);

  const handleDesativarCampanha = async () => {
    setLoading(true);
    try {
      const response = await apiClient.put(
        `/campanhas-voucher/${campanha.id}/desativar`
      );
      if (response?.dados) {
        toast.success("Campanha desativada com sucesso!");
        onSuccess();
        toggle();
      } else {
        toast.error(response?.mensagem || "Erro ao desativar campanha");
      }
    } catch (error) {
      toast.error("Erro ao desativar campanha");
    }
    setLoading(false);
    setModalConfirmacaoOpen(false);
  };

  return (
    <>
      <Offcanvas
        isOpen={isOpen}
        toggle={toggle}
        direction="end"
        className="w-50"
      >
        <OffcanvasHeader toggle={toggle}>
          <span className="fw-semibold">
            {campanha?.ativo ? "Editar Campanha" : "Visualizar Campanha"}
          </span>
        </OffcanvasHeader>
        <OffcanvasBody>
          {!campanha?.ativo ? (
            <Alert color="warning" className="mb-4">
              Esta campanha está desativada. Não é possível fazer alterações.
            </Alert>
          ) : (
            <div className="d-flex justify-content-end mb-4">
              <Button
                color="danger"
                size="sm"
                className="d-flex align-items-center"
                onClick={toggleModalConfirmacao}
                disabled={loading}
              >
                <i className="ri-delete-bin-line me-1"></i>
                Desativar Campanha
              </Button>
            </div>
          )}

          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "info" })}
                onClick={() => setActiveTab("info")}
                role="button"
              >
                Informações
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "vouchers" })}
                onClick={() => setActiveTab("vouchers")}
                role="button"
              >
                Vouchers
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="info">
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label for="nome" className="fw-medium">
                    Nome da Campanha <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    disabled={!campanha?.ativo}
                    maxLength={255}
                  />
                </FormGroup>

                <FormGroup className="mt-3">
                  <Label for="descricao" className="fw-medium">
                    Descrição
                    <small className="text-muted ms-2">
                      ({formData.descricao.length}/600 caracteres)
                    </small>
                  </Label>
                  <Input
                    id="descricao"
                    name="descricao"
                    type="textarea"
                    rows="3"
                    value={formData.descricao}
                    onChange={handleChange}
                    maxLength={600}
                    disabled={!campanha?.ativo}
                  />
                </FormGroup>

                <FormGroup className="mt-3">
                  <Label for="validade" className="fw-medium">
                    Data de Validade <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="validade"
                    name="validade"
                    type="date"
                    value={formData.validade}
                    onChange={handleChange}
                    required
                    disabled={!campanha?.ativo}
                  />
                </FormGroup>

                <div className="mt-4">
                  <Button
                    type="submit"
                    color="info"
                    className="me-2"
                    disabled={loading || !campanha?.ativo}
                  >
                    {loading && <Spinner size="sm" className="me-2" />}
                    Salvar Alterações
                  </Button>
                </div>
              </Form>
            </TabPane>

            <TabPane tabId="vouchers">
              {campanha?.ativo ? (
                <div className="mb-4">
                  <h6 className="fw-medium mb-3">Criar Novo Voucher</h6>
                  <Form onSubmit={handleCriarVoucher}>
                    <div className="row">
                      <div className="col-md-6">
                        <FormGroup>
                          <Label for="tipo" className="fw-medium">
                            Tipo de Voucher{" "}
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id="tipo"
                            name="tipo"
                            type="select"
                            value={novoVoucher.tipo}
                            onChange={handleVoucherChange}
                            required
                          >
                            <option value="fixo">Código Fixo</option>
                            <option value="aleatorio">Código Aleatório</option>
                          </Input>
                        </FormGroup>
                      </div>

                      <div className="col-md-6">
                        <FormGroup>
                          <Label for="parceiro_id" className="fw-medium">
                            Parceiro
                          </Label>
                          <Input
                            id="parceiro_id"
                            name="parceiro_id"
                            type="select"
                            value={novoVoucher.parceiro_id}
                            onChange={handleVoucherChange}
                          >
                            <option value="">Selecione um parceiro</option>
                            {parceiros.map((parceiro) => (
                              <option key={parceiro.id} value={parceiro.id}>
                                {parceiro.nome}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </div>

                      {novoVoucher.tipo === "fixo" && (
                        <div className="col-md-12">
                          <FormGroup>
                            <Label for="codigo" className="fw-medium">
                              Código do Voucher{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <Input
                              id="codigo"
                              name="codigo"
                              type="text"
                              value={novoVoucher.codigo}
                              onChange={handleVoucherChange}
                              required={novoVoucher.tipo === "fixo"}
                              placeholder="Ex: BLACKFRIDAY2024"
                              maxLength={20}
                            />
                          </FormGroup>
                        </div>
                      )}

                      <div className="col-md-6">
                        <FormGroup>
                          <Label for="valor" className="fw-medium">
                            Valor <span className="text-danger">*</span>
                          </Label>
                          <NumericFormat
                            className="form-control"
                            id="valor"
                            name="valor"
                            placeholder="R$ 0,00"
                            value={novoVoucher.valor}
                            onValueChange={({ value }) =>
                              handleVoucherChange({
                                target: {
                                  name: 'valor',
                                  value: parseFloat(value)
                                }
                              })
                            }
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale={true}
                            prefix="R$ "
                            allowNegative={false}
                          />
                        </FormGroup>
                      </div>

                      <div className="col-md-6">
                        <FormGroup>
                          <Label for="quantidade" className="fw-medium">
                            Quantidade <span className="text-danger">*</span>
                          </Label>
                          <Input
                            id="quantidade"
                            name="quantidade"
                            type="number"
                            value={novoVoucher.quantidade}
                            onChange={handleVoucherChange}
                            required
                            min="1"
                          />
                        </FormGroup>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      color="info"
                      className="mt-3"
                      disabled={loading}
                    >
                      {loading && <Spinner size="sm" className="me-2" />}
                      Criar Voucher
                    </Button>
                  </Form>
                </div>
              ) : (
                <Alert color="warning" className="mb-4">
                  Não é possível criar novos vouchers em uma campanha
                  desativada.
                </Alert>
              )}

              <div className="mt-4">
                <h6 className="fw-medium mb-3">Vouchers da Campanha</h6>
                {campanha?.vouchers?.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Código</th>
                          <th>Valor</th>
                          <th>Disponíveis</th>
                          <th>Total</th>
                          <th>Parceiro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campanha.vouchers.map((voucher) => (
                          <tr key={voucher.id}>
                            <td>{voucher.codigo}</td>
                            <td>
                              {voucher.valor
                                ? `R$ ${parseFloat(voucher.valor).toFixed(2)}`
                                : "-"}
                            </td>
                            <td>{voucher.quantidade}</td>
                            <td>{voucher.quantidade_original}</td>
                            <td>
                              {parceiros.find(
                                (p) => p.id === voucher.parceiro_id
                              )?.nome || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    Nenhum voucher criado para esta campanha
                  </div>
                )}
              </div>
            </TabPane>
          </TabContent>
        </OffcanvasBody>
      </Offcanvas>

      <ModalConfirmacao
        isOpen={modalConfirmacaoOpen}
        toggle={toggleModalConfirmacao}
        onConfirm={handleDesativarCampanha}
        title="Desativar Campanha"
        message="Tem certeza que deseja desativar esta campanha? Esta ação não poderá ser desfeita."
      />
    </>
  );
};

export default SidebarCampanha;
